import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { OrgEntry } from 'src/app/auth/auth.model';
import { orgEntriesSelector } from 'src/app/auth/store/auth.selectors';
import { State } from 'src/app/store';
import { DEFAULT_LANG } from 'src/config/lang.config';

const CITY_SLUG_PREFIXES = ['dev-admin-', 'uat-admin-', 'admin-'];

/**
 * Extracts the city slug from a hostname by stripping known admin prefixes.
 * Returns null for generic URLs (no city-specific subdomain detected).
 */
export function extractCitySlug(hostname: string): string | null {
    const subdomain = hostname.split('.')[0];
    for (const prefix of CITY_SLUG_PREFIXES) {
        if (subdomain.startsWith(prefix)) {
            const slug = subdomain.slice(prefix.length);
            return slug || null;
        }
    }
    return null;
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    public orgOptions: { label: string; value: OrgEntry }[] = [];
    public selectedOption: any = null;
    public selectedOrg: OrgEntry | null = null;
    public orgsLoading = true;
    public autoLoginInProgress = false;

    constructor(
        private store: Store<State>,
        private oidcService: OidcSecurityService,
        private route: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        // If reloaded with ?authorize=<clientId>, config is now loaded — trigger authorize immediately
        const authorizeClientId = this.route.snapshot.queryParams.authorize as string | undefined;
        if (authorizeClientId) {
            this.autoLoginInProgress = true;
            let lang: string = DEFAULT_LANG;
            try {
                const stored = localStorage.getItem('bciti-language');
                if (stored) {
                    lang = JSON.parse(stored);
                }
            } catch {
                lang = localStorage.getItem('bciti-language') || DEFAULT_LANG;
            }
            this.oidcService.authorize(authorizeClientId, { customParams: { lang } });
            return;
        }

        // Detect city-specific URL early to show spinner immediately (before orgs load)
        const slug = extractCitySlug(this.getHostname());
        const manualLogout = sessionStorage.getItem('oidc-manual-logout');
        if (manualLogout) {
            sessionStorage.removeItem('oidc-manual-logout');
        }
        if (slug && !manualLogout) {
            this.autoLoginInProgress = true;
        }

        this.store
            .select(orgEntriesSelector)
            .pipe(
                takeUntil(this.destroy$),
                filter((orgs) => orgs.length > 0),
                take(1),
            )
            .subscribe((orgs) => {
                this.orgOptions = orgs.map((org) => ({ label: org.displayName, value: org }));
                this.orgsLoading = false;

                this.attemptAutoLogin(orgs, slug);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onOrgChange(option: { label: string; value: OrgEntry }): void {
        this.selectedOrg = option?.value ?? null;
    }

    login(): void {
        if (!this.selectedOrg) {
            return;
        }
        this.loginAsOrg(this.selectedOrg);
    }

    private attemptAutoLogin(orgs: OrgEntry[], slug: string | null): void {
        if (!slug || !this.autoLoginInProgress) {
            return;
        }

        const matchedOrg = orgs.find((org) => org.subdomain === slug);
        if (!matchedOrg) {
            // Slug didn't match any org — fall back to showing dropdown
            this.autoLoginInProgress = false;
            return;
        }

        this.loginAsOrg(matchedOrg);
    }

    private loginAsOrg(org: OrgEntry): void {
        // Clear any previous OIDC session to avoid stale tokens from a different org
        const previousClientId = localStorage.getItem('oidc-active-clientId');
        if (previousClientId && previousClientId !== org.clientId) {
            this.oidcService.logoffLocal(previousClientId);
        }

        localStorage.setItem('oidc-active-clientId', org.clientId);
        sessionStorage.setItem('oidc-pending-org', org.orgTag);
        // Reload so the OIDC library picks up the new single config, then auto-authorize
        this.redirect(`/login?authorize=${org.clientId}`);
    }

    private getHostname(): string {
        return window.location.hostname;
    }

    private redirect(url: string): void {
        window.location.href = url;
    }
}
